import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { subDays } from "date-fns";
import {
  DocumentType,
  OrganizationType,
  ProjectType,
  SharingInvitationStatus,
  UserRole,
} from "../__generated__/graphql";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const organizationTypeToName: Record<OrganizationType, string> = {
  [OrganizationType.Artist]: "Artiste",
  [OrganizationType.Producer]: "Producteur / Productrice",
  [OrganizationType.Agency]: "Agence artistique",
  [OrganizationType.Other]: "Autre",
};

export const userRoleToName: Record<UserRole, string> = {
  [UserRole.AccountHolder]: "Administrateur",
  [UserRole.Admin]: "Administrateur",
  [UserRole.Member]: "Membre",
};

export const formatDate = (date: string | number | Date | null | undefined) => {
  if (!date) return null;

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  };

  return new Date(date).toLocaleDateString("fr-FR", options);
};

export const formatDateWithDay = (date: string | number | Date) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  };

  return (
    new Date(date)
      .toLocaleDateString("fr-FR", options)
      .charAt(0)
      .toUpperCase() +
    new Date(date).toLocaleDateString("fr-FR", options).slice(1)
  );
};

export const formatWeekDay = (date: string | number | Date) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
  };

  return (
    new Date(date)
      .toLocaleDateString("fr-FR", options)
      .charAt(0)
      .toUpperCase() +
    new Date(date).toLocaleDateString("fr-FR", options).slice(1)
  );
};

export const formatMonth = (
  date: string | number | Date,
  displayYear = true
) => {
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    year: displayYear ? "numeric" : undefined,
  };

  return (
    new Date(date)
      .toLocaleDateString("fr-FR", options)
      .charAt(0)
      .toUpperCase() +
    new Date(date).toLocaleDateString("fr-FR", options).slice(1)
  );
};

export const formatTime = (date: string | number | Date) => {
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  };
  return new Date(date).toLocaleTimeString("fr-FR", options);
};

export const formatDay = (date: string | number | Date) => {
  // YYYY-MM-DD format
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  };
  const day = new Date(date).toLocaleDateString("fr-FR", options);

  return day.split("/").reverse().join("-");
};

export const formatDateForInput = (
  dateString: string | null | undefined
): {
  day: string;
  time: string;
} | null => {
  if (!dateString) return null;

  try {
    return {
      day: formatDay(dateString), // YYYY-MM-DD
      time: formatTime(dateString), // HH:mm
    };
  } catch {
    console.error("Invalid date format:", dateString);
    return { day: "", time: "" };
  }
};

export const formatTimeRange = (startDate: string, endDate: string) => {
  const startHour = formatTime(startDate);
  const endHour = endDate ? formatTime(endDate) : null;

  if (endHour) {
    return `${startHour} - ${endHour}`;
  }

  return startHour;
};

export const formatDayOfWeek = (date: string | number | Date) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
  };

  return (
    new Date(date)
      .toLocaleDateString("fr-FR", options)
      .charAt(0)
      .toUpperCase() +
    new Date(date).toLocaleDateString("fr-FR", options).slice(1)
  );
};

export const statusToName: Record<SharingInvitationStatus, string> = {
  [SharingInvitationStatus.Pending]: "En attente",
  [SharingInvitationStatus.Expired]: "Expiré",
  [SharingInvitationStatus.Redeemed]: "Accepté",
  [SharingInvitationStatus.Refused]: "Refusé",
};

export const projectTypeToName: Record<ProjectType, string> = {
  [ProjectType.Album]: "Album",
  [ProjectType.Movie]: "Film",
  [ProjectType.Other]: "Autre",
  [ProjectType.Serie]: "Tournée",
};

export const documentTypeToName: Record<DocumentType, string> = {
  [DocumentType.Communication]: "Communication",
  [DocumentType.DeclarationSacd]: "Déclaration SACD",
  [DocumentType.FicheDePaie]: "Fiche de paie",
  [DocumentType.Bordereau]: "Bordereau",
  [DocumentType.JourAJour]: "Jour à jours",
  [DocumentType.Scenario]: "Scénario",
  [DocumentType.Other]: "Autre",
};

export const documentTypeStringToDocumentType: Record<string, DocumentType> = {
  communication: DocumentType.Communication,
  declaration: DocumentType.DeclarationSacd,
  bordereau: DocumentType.Bordereau,
  scenario: DocumentType.Scenario,
  fiche_de_paie: DocumentType.FicheDePaie,
  jour_a_jour: DocumentType.JourAJour,
  other: DocumentType.Other,
};

export const YESTERDAY = subDays(new Date(), 1).toISOString();

export const openGoogleMaps = (latitude: number, longitude: number) => {
  const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

export enum DocumentPropertiesType {
  FEED_POST = "feed_post",
  REEL = "reel",
  STORY = "story",
  POSTER = "poster",
  TEASER = "teaser",
  PROMO = "promo",
  OTHER = "other",
}

export const communicationTypeToName = (
  communicationType: DocumentPropertiesType
): string | null => {
  switch (communicationType) {
    case DocumentPropertiesType.FEED_POST:
      return "Publication";
    case DocumentPropertiesType.REEL:
      return "Reel";
    case DocumentPropertiesType.STORY:
      return "Story";
    case DocumentPropertiesType.POSTER:
      return "Affiche";
    case DocumentPropertiesType.TEASER:
      return "Teaser";
    case DocumentPropertiesType.PROMO:
      return "Vidéo promo";
    case DocumentPropertiesType.OTHER:
      return "Autre";
    default:
      return null;
  }
};
